export interface LatLng {
  lat: number;
  lng: number;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function lerpHeading(from: number, to: number, t: number): number {
  const diff = ((to - from + 540) % 360) - 180;
  return (from + diff * t + 360) % 360;
}

export function smoothLatLng(from: LatLng, to: LatLng, factor = 0.18): LatLng {
  return {
    lat: lerp(from.lat, to.lat, factor),
    lng: lerp(from.lng, to.lng, factor),
  };
}

/**
 * Accuracy-weighted EMA: better (lower) accuracy → higher blend toward new fix.
 * accuracyM ~10 → factor ≈ base*1.2; accuracyM ~80 → factor ≈ base*0.25
 */
export function emaLatLng(
  from: LatLng,
  to: LatLng,
  accuracyM: number | null | undefined,
  baseFactor = 0.28
): LatLng {
  let factor = baseFactor;
  if (accuracyM != null && Number.isFinite(accuracyM) && accuracyM > 0) {
    // Map accuracy 8–100m → weight 1.15–0.2
    const weight = Math.max(0.2, Math.min(1.15, 18 / Math.max(8, accuracyM)));
    factor = Math.min(0.85, baseFactor * weight);
  }
  return smoothLatLng(from, to, factor);
}

/** Max plausible travel distance given elapsed time (m/s cap for motorcycle). */
export function maxPlausibleJumpMeters(elapsedMs: number, maxSpeedMps = 45): number {
  const dt = Math.max(0.05, elapsedMs / 1000);
  // Allow a bit of slack for GPS noise
  return Math.max(25, maxSpeedMps * dt * 1.35);
}

/**
 * Reject impossible teleports. Returns true if the jump should be ignored.
 */
export function shouldRejectJump(
  from: LatLng | null,
  to: LatLng,
  opts: {
    movedM: number;
    elapsedMs: number;
    accuracyM?: number | null;
    prevAccuracyM?: number | null;
  }
): boolean {
  if (!from) return false;
  const { movedM, elapsedMs, accuracyM, prevAccuracyM } = opts;

  if (movedM > 800 && (accuracyM == null || accuracyM > 50)) return true;
  if (movedM > maxPlausibleJumpMeters(elapsedMs) && (accuracyM == null || accuracyM > 25)) {
    return true;
  }
  // New fix much worse than last and jumps far → reject
  if (
    prevAccuracyM != null &&
    accuracyM != null &&
    accuracyM > prevAccuracyM * 2.2 &&
    accuracyM > 45 &&
    movedM > 40
  ) {
    return true;
  }
  return false;
}

/** Offset a point along a heading by meters (look-ahead). */
export function offsetByHeading(origin: LatLng, headingDeg: number, meters: number): LatLng {
  if (!meters || !Number.isFinite(meters)) return origin;
  const R = 6371000;
  const brng = (headingDeg * Math.PI) / 180;
  const lat1 = (origin.lat * Math.PI) / 180;
  const lng1 = (origin.lng * Math.PI) / 180;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(meters / R) +
      Math.cos(lat1) * Math.sin(meters / R) * Math.cos(brng)
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(meters / R) * Math.cos(lat1),
      Math.cos(meters / R) - Math.sin(lat1) * Math.sin(lat2)
    );
  return {
    lat: (lat2 * 180) / Math.PI,
    lng: (lng2 * 180) / Math.PI,
  };
}

export function distanceMeters(a: LatLng, b: LatLng): number {
  const google = (window as any).google;
  if (google?.maps?.geometry?.spherical) {
    return google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(a.lat, a.lng),
      new google.maps.LatLng(b.lat, b.lng)
    );
  }
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function computeHeading(from: LatLng, to: LatLng): number {
  const google = (window as any).google;
  if (google?.maps?.geometry?.spherical) {
    return google.maps.geometry.spherical.computeHeading(
      new google.maps.LatLng(from.lat, from.lng),
      new google.maps.LatLng(to.lat, to.lng)
    );
  }
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

export function formatEta(minutes: number): string {
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** Find closest point index on a path to a position */
export function closestPointIndex(path: LatLng[], point: LatLng): number {
  if (!path.length) return 0;
  let best = 0;
  let bestDist = Infinity;
  path.forEach((p, i) => {
    const d = distanceMeters(p, point);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  });
  return best;
}
